import {
  CreateQuoteDocument,
  DocumentSource,
  ImportDocumentRequest,
  QuoteDocumentConfig,
  QuoteDocumentConfigPositionEnum,
  UpdateQuoteDocument,
} from '@monetize/types/app';
import { nullifyEmptyStrings } from '@monetize/utils/core';

/** Sentinel marking the Before/After boundary inside a single ordered document list. */
export const QUOTE_DIVIDER = { __divider: true } as const;

export type QuoteDocumentListItem = QuoteDocumentConfig | typeof QUOTE_DIVIDER;

export const isQuoteDivider = (
  item: QuoteDocumentListItem,
): item is typeof QUOTE_DIVIDER => '__divider' in item;

/**
 * Splits a single ordered list (with one divider marking the Before/After
 * boundary) back into BEFORE/AFTER position + sortWithinPosition updates,
 * ready for the existing reorder mutation.
 */
export function buildReorderPayload(
  items: QuoteDocumentListItem[],
): UpdateQuoteDocument[] {
  const dividerIndex = items.findIndex(isQuoteDivider);
  const boundary = dividerIndex === -1 ? items.length : dividerIndex;
  const before = items.slice(0, boundary) as QuoteDocumentConfig[];
  const after = items.slice(boundary + 1) as QuoteDocumentConfig[];

  const toUpdate = (
    doc: QuoteDocumentConfig,
    position: QuoteDocumentConfigPositionEnum,
    sortWithinPosition: number,
  ): UpdateQuoteDocument => ({
    id: doc.id,
    documentTypeId: doc.documentTypeId,
    position,
    sortWithinPosition,
    autoAddToQuotes: doc.autoAddToQuotes,
  });

  return [
    ...before.map((doc, index) =>
      toUpdate(doc, QuoteDocumentConfigPositionEnum.BEFORE, index),
    ),
    ...after.map((doc, index) =>
      toUpdate(doc, QuoteDocumentConfigPositionEnum.AFTER, index),
    ),
  ];
}

/**
 * Reinserts a document into a sorted list at the index implied by its
 * sortWithinPosition — used to keep Replace's row visible in its original
 * slot for the moment where the delete step has removed it from the server
 * list but the create step hasn't landed yet.
 */
export function insertBySortOrder(
  docs: QuoteDocumentConfig[],
  doc: QuoteDocumentConfig,
): QuoteDocumentConfig[] {
  const insertIndex = docs.findIndex(
    (existing) => existing.sortWithinPosition > doc.sortWithinPosition,
  );
  const next = [...docs];
  next.splice(insertIndex === -1 ? docs.length : insertIndex, 0, doc);
  return next;
}

export interface SortPositionReserver {
  reserveFor(
    localId: string,
    position: QuoteDocumentConfigPositionEnum,
  ): number;
  sync(
    beforeDocuments: QuoteDocumentConfig[],
    afterDocuments: QuoteDocumentConfig[],
  ): void;
}

const nextSafeSortWithinPosition = (docs: QuoteDocumentConfig[]): number =>
  docs.reduce((max, doc) => Math.max(max, doc.sortWithinPosition), -1) + 1;

/**
 * Hands out unique, increasing sortWithinPosition values for pending
 * uploads at the moment their document type is picked, instead of reading
 * beforeDocuments/afterDocuments.length props — which only reflect the last
 * committed create+refetch round trip and go stale the instant a second
 * file's type is picked before the first upload resolves. Reservations are
 * keyed by the upload's localId so a retry reuses the original slot rather
 * than jumping to the back of the queue.
 */
export function createSortPositionReserver(): SortPositionReserver {
  const counters: Record<QuoteDocumentConfigPositionEnum, number> = {
    [QuoteDocumentConfigPositionEnum.BEFORE]: 0,
    [QuoteDocumentConfigPositionEnum.AFTER]: 0,
  };
  const reserved = new Map<string, number>();

  return {
    reserveFor(localId, position) {
      const existing = reserved.get(localId);
      if (existing !== undefined) {
        return existing;
      }
      const value = counters[position];
      reserved.set(localId, value);
      counters[position] = value + 1;
      return value;
    },
    sync(beforeDocuments, afterDocuments) {
      counters[QuoteDocumentConfigPositionEnum.BEFORE] = Math.max(
        counters[QuoteDocumentConfigPositionEnum.BEFORE],
        nextSafeSortWithinPosition(beforeDocuments),
      );
      counters[QuoteDocumentConfigPositionEnum.AFTER] = Math.max(
        counters[QuoteDocumentConfigPositionEnum.AFTER],
        nextSafeSortWithinPosition(afterDocuments),
      );
    },
  };
}

/** Preserves the removed document's type/position/autoAdd when Replace swaps in a new file. */
export function buildReplacementCreatePayload(
  doc: QuoteDocumentConfig,
): CreateQuoteDocument {
  return {
    documentTypeId: doc.documentTypeId,
    position: doc.position,
    sortWithinPosition: doc.sortWithinPosition,
    autoAddToQuotes: doc.autoAddToQuotes,
  };
}

export function humanizeDocumentSource(source: DocumentSource): string {
  return source
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Row-level Source label: was this uploaded directly on the quote, or pulled from a library? */
export function getRowSourceLabel(originalSource: DocumentSource): string {
  return originalSource === DocumentSource.QUOTE
    ? 'Manually Uploaded'
    : 'Library';
}

/**
 * Manually uploaded (QUOTE-sourced) documents always import after the quote;
 * library documents (legal entity/tenant) keep their own before/after position.
 */
export function resolveImportPosition(
  doc: QuoteDocumentConfig,
): QuoteDocumentConfigPositionEnum {
  return doc.originalSource === DocumentSource.QUOTE
    ? QuoteDocumentConfigPositionEnum.AFTER
    : doc.position;
}

/** Builds the import request payload for the documents the user checked in the library popover. */
export function buildImportPayload(
  selectedIds: Set<string>,
  availableDocuments: QuoteDocumentConfig[],
): ImportDocumentRequest[] {
  return availableDocuments
    .filter((doc) => selectedIds.has(doc.id))
    .map((doc) => ({
      documentConfigId: doc.id,
      parent: doc.parent,
      position: resolveImportPosition(doc),
    }));
}

/**
 * The import endpoint doesn't reliably append newly imported documents to
 * the end of their position bucket — it can carry over the library
 * document's own sortWithinPosition, dropping it in the middle of the
 * quote's existing documents. Rebuilds the full order with previously
 * existing documents kept in place and newly imported ones pushed to the
 * end of their bucket, ready for the reorder mutation. Returns null when
 * nothing new is present (e.g. the refetch raced an unrelated update).
 */
export function buildImportReorderPayload(
  refreshedConfigs: QuoteDocumentConfig[],
  previousIds: Set<string>,
): UpdateQuoteDocument[] | null {
  const newDocs = refreshedConfigs.filter((doc) => !previousIds.has(doc.id));
  if (newDocs.length === 0) {
    return null;
  }

  const buildBucket = (position: QuoteDocumentConfigPositionEnum) => {
    const existing = refreshedConfigs
      .filter((doc) => doc.position === position && previousIds.has(doc.id))
      .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition);
    const added = newDocs
      .filter((doc) => doc.position === position)
      .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition);
    return [...existing, ...added];
  };

  const fullOrder: QuoteDocumentListItem[] = [
    ...buildBucket(QuoteDocumentConfigPositionEnum.BEFORE),
    QUOTE_DIVIDER,
    ...buildBucket(QuoteDocumentConfigPositionEnum.AFTER),
  ];

  return buildReorderPayload(fullOrder);
}

const orderKey = (item: QuoteDocumentListItem): string =>
  isQuoteDivider(item) ? '__divider__' : item.id;

/**
 * Whether two ordered lists represent the same Before/After arrangement —
 * used to skip the reorder mutation when a drag ends up back where it started.
 */
export function haveSameOrder(
  a: QuoteDocumentListItem[],
  b: QuoteDocumentListItem[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((item, index) => orderKey(item) === orderKey(b[index]));
}

export interface ReplaceDocumentDeps {
  beforeDocuments: QuoteDocumentConfig[];
  afterDocuments: QuoteDocumentConfig[];
  deleteDocument: (documentConfigId: string) => Promise<unknown>;
  createDocument: (
    data: CreateQuoteDocument,
    file: File,
  ) => Promise<QuoteDocumentConfig[] | undefined>;
  persistReorder: (docs: UpdateQuoteDocument[]) => Promise<unknown>;
}

/**
 * Delete-then-create-then-explicit-reorder choreography for Replace, shared
 * across the Quote page and Settings — the create endpoint doesn't reliably
 * honor the deleted doc's sortWithinPosition once delete has shifted
 * everyone else down, so this captures the original slot and reorders the
 * replacement back into it once created.
 */
export async function orchestrateDocumentReplace(
  doc: QuoteDocumentConfig,
  file: File,
  deps: ReplaceDocumentDeps,
): Promise<void> {
  const {
    beforeDocuments,
    afterDocuments,
    deleteDocument,
    createDocument,
    persistReorder,
  } = deps;

  const bucket =
    doc.position === QuoteDocumentConfigPositionEnum.BEFORE
      ? beforeDocuments
      : afterDocuments;
  const originalIndex = bucket.findIndex((existing) => existing.id === doc.id);
  const previousIds = new Set(
    [...beforeDocuments, ...afterDocuments].map((existing) => existing.id),
  );

  await deleteDocument(doc.id);
  const refreshedConfigs = await createDocument(
    buildReplacementCreatePayload(doc),
    file,
  );

  if (originalIndex === -1 || !refreshedConfigs) {
    return;
  }

  const newDoc = refreshedConfigs.find((config) => !previousIds.has(config.id));
  if (!newDoc) {
    return;
  }

  const sameBucket = refreshedConfigs
    .filter(
      (config) => config.position === doc.position && config.id !== newDoc.id,
    )
    .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition);
  sameBucket.splice(Math.min(originalIndex, sameBucket.length), 0, newDoc);

  const otherBucket = refreshedConfigs
    .filter((config) => config.position !== doc.position)
    .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition);

  const isBefore = doc.position === QuoteDocumentConfigPositionEnum.BEFORE;
  const fullOrder: QuoteDocumentListItem[] = [
    ...(isBefore ? sameBucket : otherBucket),
    QUOTE_DIVIDER,
    ...(isBefore ? otherBucket : sameBucket),
  ];

  await persistReorder(buildReorderPayload(fullOrder));
}

/** Shared multipart shape for every create-document call site (fresh upload or Replace). */
export function buildDocumentUploadFormData(
  data: CreateQuoteDocument,
  file: File,
): FormData {
  const formData = new FormData();
  formData.append('file', file);
  formData.append(
    'data',
    new Blob([JSON.stringify(nullifyEmptyStrings(data))], {
      type: 'application/json',
    }),
  );
  return formData;
}

/**
 * Settings has no single-doc update endpoint — only a batch endpoint taking
 * the full configurations array. Builds the one-element array for a
 * single-field inline edit (Type change, Add to New Quotes toggle), preserving the
 * doc's other fields.
 */
export function buildSingleDocumentUpdate(
  doc: QuoteDocumentConfig,
  overrides: Partial<
    Pick<UpdateQuoteDocument, 'documentTypeId' | 'autoAddToQuotes'>
  >,
): UpdateQuoteDocument[] {
  return [
    {
      id: doc.id,
      documentTypeId: doc.documentTypeId,
      position: doc.position,
      sortWithinPosition: doc.sortWithinPosition,
      autoAddToQuotes: doc.autoAddToQuotes,
      ...overrides,
    },
  ];
}
